


import React, { useState, useEffect, useRef } from 'react';
import { Button } from './Button';
import { Multometro } from './Multometro';

interface HeaderProps {
  isMenuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
  onOpenPricesModal: () => void;
  onOpenDelegationsModal: () => void;
  onOpenFranchiseModal: () => void;
  onOpenJobsModal: () => void;
  onOpenLoginModal: () => void;
  onOpenRegisterModal: () => void;
  onOpenCreaAdModal: () => void;
  onOpenFaqModal: () => void;
  onOpenSuccessStoriesModal: () => void;
  onOpenCountryGuideModal: () => void;
  onOpenContactModal: () => void;
  onOpenDenunciaModal: () => void;
  onOpenNormativasVariasModal: () => void;
  onOpenPartnersModal: () => void;
}


export const Header: React.FC<HeaderProps> = ({
  isMenuOpen,
  onToggleMenu,
  onCloseMenu,
  onOpenPricesModal,
  onOpenDelegationsModal,
  onOpenFranchiseModal,
  onOpenJobsModal,
  onOpenLoginModal,
  onOpenRegisterModal,
  onOpenCreaAdModal,
  onOpenFaqModal,
  onOpenSuccessStoriesModal,
  onOpenCountryGuideModal,
  onOpenContactModal,
  onOpenDenunciaModal,
  onOpenNormativasVariasModal,
  onOpenPartnersModal,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close if click is outside of menu panel
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        // Also check if the click is not on the toggle button itself
        const toggleButton = document.getElementById('hamburger-toggle');
        if (toggleButton && !toggleButton.contains(event.target as Node)) {
          onCloseMenu();
        }
      }
    };
    
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
      // Use a timeout to avoid immediate closing due to the same click event that opened the menu
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, onCloseMenu]);
  

  const menuButtonClasses = "w-full text-left px-4 py-2.5 text-base font-medium text-slate-300 rounded-lg hover:bg-slate-700 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500";
  const menuAnchorClasses = `${menuButtonClasses} block`;
  const menuPlaceholderClasses = `${menuButtonClasses} opacity-50 cursor-not-allowed`;
  const menuHeaderClasses = "px-4 pt-3 pb-1 text-xs font-semibold text-amber-400 uppercase tracking-wider";

  const headerClasses = `sticky top-0 z-30 transition-all duration-300 ${hasScrolled ? 'bg-slate-900/80 backdrop-blur-md shadow-lg' : 'bg-transparent'}`;

  return (
    <>
      <header className={headerClasses}>
        <div className="container mx-auto px-6 py-2 flex justify-between items-center">
          {/* Logo and Title Section */}
          <div className="flex items-center">
              <a href="/" className="flex-shrink-0" aria-label="Página de inicio de Multas.EU">
                  <img src="https://datos.us/g/multas.eu/graphs/Logo_multas.eu_2025-06-26_10-41-03.png" alt="Multas.EU Logo" className="h-12 mr-3" />
              </a>
              <div className="flex items-baseline flex-wrap">
                  <h1 className="text-2xl font-bold mr-2">
                      <a href="/" className="text-white hover:text-slate-200 transition-colors">
                          Multas<span className="text-yellow-400">.EU</span>
                      </a>
                  </h1>
                  <span className="text-base text-slate-400 hidden sm:inline">
                    (con <span className="font-bold text-amber-500">Peritajes</span><span className="font-bold text-yellow-400">.EU</span> y <span className="font-bold text-red-500">Juzgados</span><span className="font-bold text-yellow-400">.EU</span>)
                  </span>
              </div>
          </div>
          
          {/* Placeholder for Hamburger Menu Button to maintain layout */}
           <div className="w-16 h-16 shrink-0" aria-hidden="true" />
        </div>
      </header>
      
      {/* Hamburger Menu Button - Fixed Position */}
      <div id="hamburger-toggle" className="fixed top-2 right-6 z-50">
        <button
          onClick={onToggleMenu}
          className="relative w-16 h-16 flex items-center justify-center focus:outline-none rounded-md group animate-attention"
          aria-label={isMenuOpen ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={isMenuOpen}
        >
          <img
            src="https://datos.us/g/multas.eu/graphs/multas/menu_hamburguesa_agosto_25_crop_trans.png"
            alt="Menú"
            className={`w-14 h-14 object-contain transition-transform duration-300 ease-in-out group-hover:scale-110 ${isMenuOpen ? 'rotate-90 scale-95' : 'rotate-0 scale-100'}`}
          />
        </button>
      </div>

      {/* --- Menu Overlay and Panel --- */}
      <div className={`fixed inset-0 z-40 transition-opacity duration-300 ${isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onCloseMenu}
          aria-hidden="true"
        />
        
        {/* Menu Panel */}
        <div 
          ref={menuRef}
          className={`fixed top-0 right-0 h-full w-full max-w-md lg:max-w-[48rem] bg-slate-800 shadow-2xl transition-transform duration-300 ease-in-out transform ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
        >
          <div className="flex flex-col h-full text-white">
            
            {/* Menu Header: Language & Login */}
            <div className="p-4 border-b border-slate-700 bg-slate-900/50">
              <h3 className="text-2xl font-bold text-white mb-4">Menú</h3>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 items-center">
                <div className="lg:col-span-1">
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Idioma</label>
                  <select
                      id="language-selector"
                      name="language"
                      className="w-full mt-1 px-3 py-2 border border-slate-600 rounded-md shadow-sm focus:ring-amber-500 focus:border-amber-500 bg-slate-700 text-white transition-colors"
                  >
                      <option>Español</option>
                      <option>Catalan</option>
                      <option>Frances</option>
                      <option>Ingles</option>
                  </select>
                </div>
                <div className="flex flex-col gap-2 lg:col-span-2 lg:grid lg:grid-cols-2 lg:gap-4">
                  <Button onClick={() => { onOpenLoginModal(); onCloseMenu(); }} variant="secondary" className="w-full !bg-blue-600 hover:!bg-blue-500">
                      Iniciar Sesión
                  </Button>
                  <Button onClick={() => { onOpenRegisterModal(); onCloseMenu(); }} variant="outline" className="w-full !border-amber-500 !text-amber-500 hover:!bg-amber-500 hover:!text-slate-900">
                      Registrarse
                  </Button>
                </div>
              </div>
            </div>

            <nav className="flex-1 overflow-y-auto p-4 grid grid-cols-1 lg:grid-cols-2 gap-x-8">
              {/* Column 1 */}
              <div className="flex flex-col gap-y-1">
                  <button onClick={() => { window.location.href='/'; onCloseMenu(); }} className={menuButtonClasses}>Inicio</button>
                  <button disabled className={menuPlaceholderClasses}>Cómo Funciona</button>
                  <button onClick={() => { onOpenFaqModal(); onCloseMenu(); }} className={menuButtonClasses}>Preguntas Frecuentes (FAQ)</button>
                  <button onClick={() => { onOpenPricesModal(); onCloseMenu(); }} className={menuButtonClasses}>Precios</button>
                  <button onClick={() => { onOpenContactModal(); onCloseMenu(); }} className={menuButtonClasses}>Contacta con un Experto</button>

                  <hr className="my-3 border-slate-700" />
                  
                  <div>
                      <p className={menuHeaderClasses}>Herramientas y Conocimiento</p>
                      <div className="mt-1 flex flex-col gap-y-1">
                          <a href="https://blog.multas.org" target="_blank" rel="noopener noreferrer" className={menuAnchorClasses}>
                          Blog / Noticias
                          <span className="block text-xs text-slate-400 pl-1">Cazador de Abusos</span>
                          </a>
                          <button onClick={() => { onOpenCountryGuideModal(); onCloseMenu(); }} className={menuButtonClasses}>Guía Legal por Tipo de Multa</button>
                          <button disabled className={menuPlaceholderClasses}>Recursos y Modelos Gratis</button>
                          <button disabled className={menuPlaceholderClasses}>Calculadora de Multas</button>
                          <a href="https://blog.multas.org/trucos-2" target="_blank" rel="noopener noreferrer" className={menuAnchorClasses}>Trucos</a>
                      </div>
                  </div>
              </div>

              {/* Column 2 */}
              <div className="flex flex-col gap-y-1 mt-4 lg:mt-0">
                   <div>
                      <p className={menuHeaderClasses}>Nuestra Evidencia</p>
                      <div className="mt-1 flex flex-col gap-y-1">
                          <button onClick={() => { onOpenSuccessStoriesModal(); onCloseMenu(); }} className={menuButtonClasses}>Casos de Éxito</button>
                          <button disabled className={menuPlaceholderClasses}>Testimonios Reales / Video-casos</button>
                          <button disabled className={menuPlaceholderClasses}>Ranking de Multas Más Abusivas</button>
                      </div>
                  </div>

                  <hr className="my-3 border-slate-700" />
                  
                  <div>
                      <p className={menuHeaderClasses}>Únete a Nosotros</p>
                      <div className="mt-1 flex flex-col gap-y-1">
                          <button onClick={() => { onOpenDelegationsModal(); onCloseMenu(); }} className={menuButtonClasses}>Delegaciones / Cobertura</button>
                          <button onClick={() => { onOpenFranchiseModal(); onCloseMenu(); }} className={menuButtonClasses}>Ser Franquicia / Colabora</button>
                          <button onClick={() => { onOpenJobsModal(); onCloseMenu(); }} className={menuButtonClasses}>Trabaja con Nosotros</button>
                          <button disabled className={menuPlaceholderClasses}>Área de Prensa</button>
                          <button onClick={() => { onOpenPartnersModal(); onCloseMenu(); }} className={menuButtonClasses}>Zona Abogados / Partners</button>
                      </div>
                  </div>

                  <hr className="my-3 border-slate-700" />

                  <div>
                      <p className={menuHeaderClasses}>Comunidad</p>
                      <div className="mt-1 flex flex-col gap-y-1">
                          <button disabled className={menuPlaceholderClasses}>Canal de Telegram / Comunidad</button>
                          <button onClick={() => { onOpenDenunciaModal(); onCloseMenu(); }} className={menuButtonClasses}>Denuncia a la Administracion</button>
                          <button onClick={() => { onOpenNormativasVariasModal(); onCloseMenu(); }} className={menuButtonClasses}>Normativas Varias</button>
                          <button disabled className={menuPlaceholderClasses}>Avisos de Cambios Legislativos</button>
                      </div>
                  </div>
              </div>

              {/* Creator Section */}
              <div className="lg:col-span-2 mt-6">
                <div className="bg-green-900/70 text-white rounded-lg shadow-inner p-4 border border-green-700">
                    <h4 className="font-bold text-amber-300 mb-1">¿Eres Creador? Entra en <button onClick={() => { onOpenCreaAdModal(); onCloseMenu(); }} className="font-semibold underline hover:text-yellow-300 transition-colors bg-transparent border-none p-0 cursor-pointer">Crea.AD</button> y te ayudaremos.</h4>
                    <p className="text-sm text-slate-300 leading-relaxed">
                        Enfócate en crear, nosotros nos encargamos de los impuestos y la burocracia. Crea.AD es nuestra división especializada para Youtubers, Instagramers, Tiktokers y cualquier creador de contenido digital. Entendemos los retos únicos de tu profesión y te ofrecemos soluciones a medida para que optimices tus ingresos y trabajes con total tranquilidad.
                        <span className="block mt-2 font-semibold text-amber-400 uppercase tracking-wider text-xs">
                            Creado por creadores con conocimiento legal y técnico
                        </span>
                    </p>
                </div>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </>
  );
};